import { useEffect, useState } from "react";
import cn from "classnames";
import { useTranslation } from "react-i18next";
import { Edit } from "iconsax-react";
import { Button, Prompt } from "@/shared/ui";
import type { useEgovMobileSigning } from "../hooks/useEgovMobileSigning";
import type { useOrderSigning } from "../hooks/useOrderSigning";
import EgovMobileSigningModal from "./EgovMobileSigningModal";
import SigningMethodModal from "./SigningMethodModal";

interface Props {
  nca: ReturnType<typeof useOrderSigning>;
  mobile: ReturnType<typeof useEgovMobileSigning>;
  onNcaClick?: () => void;
  onMobileClick?: () => void;
  label?: string;
  size?: "md" | "lg" | "huge";
  hideMobile?: boolean;
  disabled?: boolean;
  className?: string;
  /** Which eGov app this signer uses; drives the labels in the picker. */
  egovVariant?: "business" | "personal";
}

/**
 * One button everywhere; the method (NCALayer vs eGov Mobile) is picked in a
 * modal. When eGov is unavailable for the document the button goes straight to
 * NCALayer instead of showing a pointless one-option dialog.
 */
export default function SigningActions({
  nca,
  mobile,
  onNcaClick,
  onMobileClick,
  label,
  size = "huge",
  hideMobile = false,
  disabled = false,
  className,
  egovVariant = "business",
}: Props) {
  const { t } = useTranslation("Common");
  const [isMethodModalOpen, setIsMethodModalOpen] = useState(false);
  const [isQrOpen, setIsQrOpen] = useState(false);

  const isStarting = nca.isLoading || mobile.isStarting;

  useEffect(
    function dropQrWithSession() {
      if (!mobile.isActive) setIsQrOpen(false);
    },
    [mobile.isActive],
  );

  function startNcaLayer() {
    setIsMethodModalOpen(false);
    (onNcaClick || nca.handleSign)();
  }

  function startEgovMobile() {
    setIsMethodModalOpen(false);
    setIsQrOpen(true);
    (onMobileClick || mobile.handleStart)();
  }

  function handleClick() {
    if (mobile.isActive) {
      setIsQrOpen(true);
      return;
    }
    if (hideMobile) {
      startNcaLayer();
      return;
    }
    setIsMethodModalOpen(true);
  }

  return (
    <>
      {isStarting && (
        <Prompt
          loaderMode={true}
          onClose={() => {}}
          title=""
          text=""
          loaderText={nca.isLoading ? nca.loaderText : t("signing.egov.phases.starting")}
          additionalText={t("signing.phases.pleaseWait")}
          onCancel={nca.isLoading && nca.canCancel ? nca.handleCancel : undefined}
          namespace="Common"
        />
      )}

      {isMethodModalOpen && (
        <SigningMethodModal
          egovVariant={egovVariant}
          onSelectNcaLayer={startNcaLayer}
          onSelectEgovMobile={startEgovMobile}
          onClose={() => setIsMethodModalOpen(false)}
        />
      )}

      {mobile.isActive && isQrOpen && (
        <EgovMobileSigningModal mobile={mobile} onClose={() => setIsQrOpen(false)} />
      )}

      <Button
        variant="primary"
        size={size}
        type="button"
        className={cn("w-full", className)}
        disabled={disabled || isStarting}
        onClick={handleClick}>
        <Edit size={16} color="currentColor" />
        {mobile.isActive ? t("signing.egov.showQr") : label || t("signing.actions.signDocument")}
      </Button>
    </>
  );
}
